import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, NativeSelect, TextField } from '@material-ui/core';

import { catalogApi } from 'api/catalog';

function sanitizeNumericValue(value) {
    const sanitized = value.replace(/[^\d.]/g, '');
    const [whole, ...fractionalParts] = sanitized.split('.');

    if (fractionalParts.length === 0) {
        return whole;
    }

    return `${whole}.${fractionalParts.join('')}`;
}

function formatNumericFieldValue(value) {
    if (value == null || value === '') {
        return '';
    }

    return String(value);
}

/**
 * Maps nutrition info and field info to editable rows
 * @param {Object[]} nutritionInfo
 * @param {Object[]} nutritionFields
 * @returns {Object[]}
 */
function mapNutritionInfoToRows(nutritionInfo, nutritionFields) {
    if (!nutritionInfo?.length) {
        return [];
    }

    const fieldIdByName = new Map(nutritionFields?.map((field) => [field.name, field.id]) ?? []);

    return nutritionInfo.map((item) => ({
        id: item.id,
        rowKey: String(item.id),
        nutritionFieldId: String(item.nutrition_field_id ?? fieldIdByName.get(item.nutrition_field_name) ?? ''),
        nutrientValue: formatNumericFieldValue(item.nutrient_value),
        nutrientUnit: item.nutrient_unit ?? '',
        percentDailyValue: formatNumericFieldValue(item.nutrient_percent_daily_value),
        extraData: item.extra_data ?? '',
        createdAt: item.created_at,
        source: item.source ?? null,
    }));
}

function getNutritionField(nutritionFields, fieldId) {
    return nutritionFields.find((nutritionField) => String(nutritionField.id) === String(fieldId));
}

function isServingSizeField(nutritionFields, fieldId) {
    return getNutritionField(nutritionFields, fieldId)?.name === 'Serving Size';
}

/**
 * Maps editable rows to payload for API updates
 * @param {Object[]} rows
 * @param {Object[]} nutritionFields
 * @returns {Object[]}
 */
function mapRowsToPayload(rows, nutritionFields) {
    return rows
        .filter((row) => row.nutritionFieldId)
        .map((row) => ({
            ...(row.id != null ? { id: row.id } : {}),
            nutritionFieldId: Number(row.nutritionFieldId),
            nutrientValue: row.nutrientValue,
            nutrientUnit: row.nutrientUnit,
            nutrientPercentDailyValue: row.percentDailyValue !== '' ? Number(row.percentDailyValue) : null,
            ...(isServingSizeField(nutritionFields, row.nutritionFieldId)
                ? { extraData: row.extraData !== '' ? row.extraData : null }
                : {}),
        }));
}

function isFieldUsedByOtherRow(rows, fieldId, rowKey) {
    return rows.some((row) => row.rowKey !== rowKey && row.nutritionFieldId === String(fieldId));
}

function nutritionFieldHasUnits(nutritionFields, fieldId) {
    return Boolean(getNutritionField(nutritionFields, fieldId)?.hasUnits);
}

function nutritionFieldHasPercentDailyValue(nutritionFields, fieldId) {
    return Boolean(getNutritionField(nutritionFields, fieldId)?.hasPercentDailyValue);
}

function canAddRow(rows, nutritionFields) {
    if (rows.some((row) => !row.nutritionFieldId)) {
        return false;
    }

    const usedFieldIds = new Set(rows.map((row) => row.nutritionFieldId));
    return usedFieldIds.size < nutritionFields.length;
}

function deriveIsHumanValidated(rows) {
    return !rows.some((row) => row.id != null && row.source === 'AI');
}

/**
 * Nutrition information component
 * @param {Object} props
 * @param {string} props.externalSku
 * @param {Object} props.nutritionResponse
 * @param {Object} ref
 * @returns {JSX.Element}
 */
const NutritionInformation = forwardRef(({ externalSku, nutritionResponse }, ref) => {
    const nutritionFields = nutritionResponse?.nutritionFields ?? [];
    const nextRowKeyRef = useRef(1);
    const [rows, setRows] = useState(() => mapNutritionInfoToRows(nutritionResponse?.nutritionInfo, nutritionFields));
    const [isHumanValidated, setIsHumanValidated] = useState(() => deriveIsHumanValidated(rows));
    const [prevNutritionResponse, setPrevNutritionResponse] = useState(nutritionResponse);

    if (nutritionResponse !== prevNutritionResponse) {
        const nextRows = mapNutritionInfoToRows(nutritionResponse?.nutritionInfo, nutritionFields);
        setPrevNutritionResponse(nutritionResponse);
        setRows(nextRows);
        setIsHumanValidated(deriveIsHumanValidated(nextRows));
    }

    const updateRow = (rowKey, field, value) => {
        if (field === 'nutritionFieldId' && value && isFieldUsedByOtherRow(rows, value, rowKey)) {
            return;
        }

        setRows((currentRows) =>
            currentRows.map((row) => {
                if (row.rowKey !== rowKey) {
                    return row;
                }

                const updatedRow = { ...row, [field]: value };

                if (field === 'nutritionFieldId') {
                    if (!isServingSizeField(nutritionFields, value)) {
                        updatedRow.extraData = '';
                    }
                    if (!nutritionFieldHasUnits(nutritionFields, value)) {
                        updatedRow.nutrientUnit = '';
                    }
                    if (!nutritionFieldHasPercentDailyValue(nutritionFields, value)) {
                        updatedRow.percentDailyValue = '';
                    }
                }

                return updatedRow;
            }),
        );
    };

    const removeRow = (rowKey) => {
        setRows((currentRows) => currentRows.filter((row) => row.rowKey !== rowKey));
    };

    const addRow = () => {
        if (!canAddRow(rows, nutritionFields)) {
            return;
        }

        const rowKey = `new-${nextRowKeyRef.current}`;
        nextRowKeyRef.current += 1;

        setRows((currentRows) => [
            ...currentRows,
            {
                id: null,
                rowKey,
                nutritionFieldId: '',
                nutrientValue: '',
                nutrientUnit: '',
                percentDailyValue: '',
                extraData: '',
                createdAt: null,
            },
        ]);
    };

    useImperativeHandle(
        ref,
        () => ({
            save: async () =>
                catalogApi.updateItemInformationNutritionInfo(
                    externalSku,
                    mapRowsToPayload(rows, nutritionFields),
                    isHumanValidated,
                ),
        }),
        [externalSku, nutritionFields, rows, isHumanValidated],
    );

    return (
        <Box data-testid="nutrition-information">
            {rows.length === 0 ? <p>No nutrition information found.</p> : null}
            {rows.map((row) => (
                <Box key={row.rowKey} mb={2} style={row.source === 'AI' ? { backgroundColor: '#fffde7' } : undefined}>
                    <Box display="flex" flexWrap="wrap" alignItems="flex-end" gridGap={8}>
                        <NativeSelect
                            label="Field Type"
                            aria-label="Nutrition field name"
                            value={row.nutritionFieldId}
                            onChange={(event) => updateRow(row.rowKey, 'nutritionFieldId', event.target.value)}
                            inputProps={{ 'data-testid': `nutrition-field-select-${row.rowKey}` }}
                        >
                            <option value="">Select field</option>
                            {nutritionFields.map((field) => (
                                <option
                                    key={field.id}
                                    value={field.id}
                                    disabled={isFieldUsedByOtherRow(rows, field.id, row.rowKey)}
                                >
                                    {field.name}
                                </option>
                            ))}
                        </NativeSelect>
                        <TextField
                            label="Value"
                            size="small"
                            value={row.nutrientValue}
                            onChange={(event) =>
                                updateRow(row.rowKey, 'nutrientValue', sanitizeNumericValue(event.target.value))
                            }
                            inputProps={{
                                'data-testid': `nutrient-value-${row.rowKey}`,
                                inputMode: 'decimal',
                            }}
                        />
                        {nutritionFieldHasUnits(nutritionFields, row.nutritionFieldId) ? (
                            <TextField
                                label="Unit"
                                size="small"
                                value={row.nutrientUnit}
                                onChange={(event) => updateRow(row.rowKey, 'nutrientUnit', event.target.value)}
                                inputProps={{ 'data-testid': `nutrient-unit-${row.rowKey}` }}
                            />
                        ) : null}
                        {nutritionFieldHasPercentDailyValue(nutritionFields, row.nutritionFieldId) ? (
                            <TextField
                                label="% Daily Value"
                                size="small"
                                value={row.percentDailyValue}
                                onChange={(event) =>
                                    updateRow(row.rowKey, 'percentDailyValue', sanitizeNumericValue(event.target.value))
                                }
                                inputProps={{
                                    'data-testid': `percent-daily-value-${row.rowKey}`,
                                    inputMode: 'decimal',
                                }}
                            />
                        ) : null}
                        {isServingSizeField(nutritionFields, row.nutritionFieldId) ? (
                            <TextField
                                label="Full Text"
                                size="small"
                                value={row.extraData}
                                onChange={(event) => updateRow(row.rowKey, 'extraData', event.target.value)}
                                inputProps={{ 'data-testid': `nutrient-full-text-${row.rowKey}` }}
                            />
                        ) : null}
                        <Button
                            color="secondary"
                            aria-label="Remove nutrition field"
                            onClick={() => removeRow(row.rowKey)}
                            data-testid={`nutrition-field-remove-${row.rowKey}`}
                        >
                            Remove
                        </Button>
                    </Box>
                </Box>
            ))}
            <Button
                color="primary"
                disabled={!canAddRow(rows, nutritionFields)}
                onClick={addRow}
                data-testid="nutrition-add-field"
            >
                Add Field
            </Button>
            {rows.length > 0 ? (
                <Box mt={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                checked={isHumanValidated}
                                onChange={(event) => setIsHumanValidated(event.target.checked)}
                            />
                        }
                        label="Human Validated"
                    />
                </Box>
            ) : null}
        </Box>
    );
});

export {
    NutritionInformation,
    canAddRow,
    deriveIsHumanValidated,
    isFieldUsedByOtherRow,
    nutritionFieldHasUnits,
    nutritionFieldHasPercentDailyValue,
    isServingSizeField,
    mapNutritionInfoToRows,
    mapRowsToPayload,
    formatNumericFieldValue,
    sanitizeNumericValue,
};
